import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

// Tek asıl adres: nginx threat.live → www.threat.live yönlendiriyor; canonical/og da www olmalı
const SITE_URL = 'https://www.threat.live';

const SEO = ({ page = 'home' }) => {
  const { language } = useLanguage();

  const seoData = {
    home: {
      tr: {
        title: 'Threat.live – Gerçek Zamanlı Tehdit İstihbaratı',
        description: 'Zararlı IP istihbarat platformu. Topluluk destekli tehdit akışları, CVE & KEV veritabanı, saldırı verileri ve BGP blackhole otomasyonu.',
        keywords: 'Threat.live, tehdit istihbaratı, zararlı IP listesi, siber güvenlik, CVE veritabanı, KEV veritabanı, BGP blackhole, DDoS tehdit, brute force, IP engelleme, ağ güvenliği, botnet koruması, saldırı engelleme, abuse IP listesi, tehdit haritası',
      },
      en: {
        title: 'Threat.live – Real-Time Threat Intelligence Platform',
        description: 'Real-time malicious IP intelligence platform providing community-driven threat feeds, CVE & KEV database, attack data and BGP blackhole automation.',
        keywords: 'Threat.live, threat intelligence, malicious IP list, abuse IP database, CVE database, KEV database, BGP blackhole, DDoS threat, brute force, IP blocklist, network security, botnet protection, cyber attack map, threat feed, IP reputation',
      }
    },
    ipList: {
      tr: {
        title: 'Zararlı IP Listesi - Threat.live | Güncel Tehdit Veritabanı',
        description: 'Threat.live tarafından tespit edilen zararlı IP adresleri listesi. DDoS, brute force, port tarama ve diğer siber saldırı kaynaklarının güncel veritabanı. MikroTik RSC formatında indirin.',
        keywords: 'zararlı IP listesi, kara liste, blacklist, IP engelleme listesi, DDoS IP listesi, brute force IP, siber tehdit veritabanı, MikroTik blacklist, firewall listesi, tehdit IP adresleri, saldırgan IP, botnet IP listesi',
      },
      en: {
        title: 'Malicious IP List - Threat.live | Real-time Threat Database',
        description: 'List of malicious IP addresses detected by Threat.live. Up-to-date database of DDoS, brute force, port scanning and other cyber attack sources. Download in MikroTik RSC format.',
        keywords: 'malicious IP list, blacklist, IP blocklist, DDoS IP list, brute force IP, cyber threat database, MikroTik blacklist, firewall list, threat IP addresses, attacker IP, botnet IP list',
      }
    },
    cve: {
      tr: {
        title: 'CVE & KEV Veritabanı - Threat.live | Güncel Zafiyet Takibi',
        description: 'Güncel CVE zafiyetleri ve CISA KEV (aktif olarak istismar edilen zafiyetler) veritabanı. Önem derecesine göre arayın, filtreleyin ve yeni zafiyetleri takip edin.',
        keywords: 'CVE veritabanı, KEV, CISA KEV, zafiyet veritabanı, güvenlik açığı, CVSS, istismar edilen zafiyetler, zero-day, yama takibi, siber güvenlik zafiyetleri',
      },
      en: {
        title: 'CVE & KEV Database - Threat.live | Vulnerability Intelligence',
        description: 'Up-to-date CVE vulnerabilities and CISA KEV (Known Exploited Vulnerabilities) database. Search, filter by severity and track newly published vulnerabilities.',
        keywords: 'CVE database, KEV, CISA KEV, vulnerability database, known exploited vulnerabilities, CVSS, security vulnerabilities, zero-day, patch tracking',
      }
    },
    map: {
      tr: {
        title: 'Canlı Siber Saldırı Haritası - Threat.live',
        description: 'Threat.live sensör ağının tespit ettiği siber saldırıları gerçek zamanlı dünya haritasında izleyin: saldırı kaynakları, hedef ülkeler ve saldırı türleri.',
        keywords: 'siber saldırı haritası, canlı saldırı haritası, tehdit haritası, DDoS haritası, gerçek zamanlı saldırılar, siber tehdit izleme',
      },
      en: {
        title: 'Live Cyber Attack Map - Threat.live',
        description: 'Watch cyber attacks detected by the Threat.live sensor network on a real-time world map: attack sources, targeted countries and attack types.',
        keywords: 'cyber attack map, live attack map, threat map, DDoS map, real-time attacks, cyber threat monitoring',
      }
    },
    stats: {
      tr: {
        title: 'Tehdit İstatistikleri - Threat.live | Saldırı Verileri',
        description: 'Threat.live tehdit istihbaratı istatistikleri: günlük tespit edilen zararlı IP sayıları, en çok saldırı yapan ASN ve ülkeler, saldırı kategorileri ve eğilimler.',
        keywords: 'tehdit istatistikleri, siber saldırı istatistikleri, zararlı IP istatistikleri, ASN istatistikleri, saldırı eğilimleri, DDoS istatistikleri',
      },
      en: {
        title: 'Threat Statistics - Threat.live | Attack Data',
        description: 'Threat.live threat intelligence statistics: daily malicious IP detections, top attacking ASNs and countries, attack categories and trends.',
        keywords: 'threat statistics, cyber attack statistics, malicious IP statistics, ASN statistics, attack trends, DDoS statistics',
      }
    },
    features: {
      tr: {
        title: 'Özellikler - Threat.live | Tehdit İstihbaratı ve BGP Blackhole',
        description: 'Threat.live özellikleri: topluluk destekli zararlı IP akışları, BGP blackhole (RTBH) otomasyonu, webhook entegrasyonları, CVE/KEV takibi ve dağıtık sensör ağı.',
        keywords: 'tehdit istihbaratı özellikleri, BGP blackhole, RTBH, IP blocklist, webhook, sensör ağı, DDoS koruması',
      },
      en: {
        title: 'Features - Threat.live | Threat Intelligence & BGP Blackhole',
        description: 'Threat.live features: community-driven malicious IP feeds, BGP blackhole (RTBH) automation, webhook integrations, CVE/KEV tracking and a distributed sensor network.',
        keywords: 'threat intelligence features, BGP blackhole, RTBH, IP blocklist, webhook, sensor network, DDoS protection',
      }
    },
    tools: {
      tr: {
        title: 'Ağ Araçları - Threat.live | Ping ve MTR Test',
        description: 'Threat.live node\'larından Ping ve MTR testleri çalıştırın. Dünya genelindeki veri merkezlerinden ağ bağlantınızı test edin ve sorunları tespit edin.',
        keywords: 'ping test, MTR test, ağ testi, network test, traceroute, bağlantı testi, latency test, gecikme testi, ağ sorunları, network diagnostics, looking glass',
      },
      en: {
        title: 'Network Tools - Threat.live | Ping and MTR Test',
        description: 'Run Ping and MTR tests from Threat.live nodes. Test your network connectivity from data centers around the world and identify issues.',
        keywords: 'ping test, MTR test, network test, traceroute, connectivity test, latency test, network issues, network diagnostics, looking glass, network troubleshooting',
      }
    },
    setup: {
      tr: {
        title: 'Kurulum Kılavuzu - Threat.live | BGP ve MikroTik Entegrasyonu',
        description: 'Threat.live kurulum rehberi. BGP peering, MikroTik RouterOS, Cisco ve diğer router\'lar için adım adım yapılandırma talimatları. Ağınızı siber tehditlere karşı koruyun.',
        keywords: 'Threat.live kurulum, BGP peering, MikroTik, RouterOS blacklist, Cisco BGP, router yapılandırma, BGP community, blackhole routing, ağ koruma kurulumu',
      },
      en: {
        title: 'Setup Guide - Threat.live | BGP and MikroTik Integration',
        description: 'Threat.live setup guide. Step-by-step configuration instructions for BGP peering, MikroTik RouterOS, Cisco and other routers. Protect your network against cyber threats.',
        keywords: 'Threat.live setup, BGP peering, MikroTik, RouterOS blacklist, Cisco BGP, router configuration, BGP community, blackhole routing, network protection setup',
      }
    },
    login: {
      tr: {
        title: 'Giriş Yap - Threat.live',
        description: 'Threat.live hesabınıza giriş yapın. BGP oturumlarınızı yönetin ve ağ güvenliği araçlarına erişin.',
        keywords: 'Threat.live giriş, Threat.live login, hesap girişi',
      },
      en: {
        title: 'Sign In - Threat.live',
        description: 'Sign in to your Threat.live account. Manage your BGP sessions and access network security tools.',
        keywords: 'Threat.live login, Threat.live sign in, account login',
      }
    },
    register: {
      tr: {
        title: 'Kayıt Ol - Threat.live',
        description: 'Threat.live\'e katılın ve ağınızı siber tehditlere karşı koruyun. Ücretsiz kayıt olun ve BGP blackhole hizmetinden yararlanın.',
        keywords: 'Threat.live kayıt, Threat.live register, ücretsiz kayıt, ağ koruma kaydı',
      },
      en: {
        title: 'Register - Threat.live',
        description: 'Join Threat.live and protect your network against cyber threats. Register for free and benefit from BGP blackhole service.',
        keywords: 'Threat.live register, Threat.live signup, free registration, network protection signup',
      }
    },
    dashboard: {
      tr: {
        title: 'Dashboard - Threat.live | BGP Oturum Yönetimi',
        description: 'Threat.live yönetim paneli. BGP oturumlarınızı yönetin, DNS testleri yapın ve ağ güvenliği araçlarına erişin.',
        keywords: 'Threat.live dashboard, BGP yönetimi, DNS test, ağ yönetimi, güvenlik paneli',
      },
      en: {
        title: 'Dashboard - Threat.live | BGP Session Management',
        description: 'Threat.live management panel. Manage your BGP sessions, run DNS tests and access network security tools.',
        keywords: 'Threat.live dashboard, BGP management, DNS test, network management, security panel',
      }
    },
    forgotPassword: {
      tr: {
        title: 'Şifremi Unuttum - Threat.live',
        description: 'Threat.live hesap şifrenizi sıfırlayın.',
        keywords: 'şifre sıfırlama, şifremi unuttum, Threat.live şifre',
      },
      en: {
        title: 'Forgot Password - Threat.live',
        description: 'Reset your Threat.live account password.',
        keywords: 'password reset, forgot password, Threat.live password',
      }
    },
    sponsors: {
      tr: {
        title: 'Altyapı Sponsorları - Threat.live',
        description: 'Threat.live algılama düğümlerine dünya çapında güç sağlayan altyapı ve kaynakları sunan kuruluşlar.',
        keywords: 'Threat.live sponsorlar, altyapı sponsorları, destek, katkıda bulunanlar',
      },
      en: {
        title: 'Infrastructure Sponsors - Threat.live',
        description: 'Organizations providing infrastructure and resources to power Threat.live detection nodes worldwide.',
        keywords: 'Threat.live sponsors, infrastructure sponsors, supporters, contributors',
      }
    }
  };

  const data = seoData[page]?.[language] || seoData[page]?.['en'] || seoData.home.en;

  return (
    // defer={false}: etiketler render anında yazılır → react-snap snapshot'ı sayfa başlığını/canonical'ı yakalar
    <Helmet defer={false}>
      <html lang={language} />
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta name="keywords" content={data.keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}${window.location.pathname}`} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:site_name" content="Threat.live" />
      <meta property="og:locale" content={language === 'tr' ? 'tr_TR' : 'en_US'} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      
      {/* Additional SEO */}
      <meta name="author" content="Threat.live" />
      <link rel="canonical" href={`${SITE_URL}${window.location.pathname}`} />
      
      {/* Alternate languages */}
      <link rel="alternate" hrefLang="tr" href={`${SITE_URL}${window.location.pathname}?lang=tr`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${window.location.pathname}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${window.location.pathname}`} />
    </Helmet>
  );
};

export default SEO;
